/**
 * Web-only PWA registration.
 *
 * Called from `index.web.tsx` after the app is running. Registers the service
 * worker emitted at `/sw.js` by `EmitPwaAssetsPlugin` (web/webpack.config.js),
 * which precaches the app shell (index.html, JS chunks, fonts, images) so the
 * app loads offline after the first visit. Skipped in development (`__DEV__`,
 * replaced at build time) and whenever the browser has no service worker
 * support (e.g. private mode) - registration is best-effort and must never
 * block the app from starting.
 */
export const registerServiceWorker = (): void => {
  if (__DEV__) {
    return;
  }

  const globalRef = globalThis as {
    addEventListener?(type: string, listener: () => void): void;
    navigator?: {
      serviceWorker?: {
        register(scriptURL: string): Promise<unknown>;
      };
    };
  };

  const register = (): void => {
    try {
      const registration =
        globalRef.navigator?.serviceWorker?.register('/sw.js');

      registration?.catch((caughtError: unknown) => {
        console.warn('Failed to register the service worker', caughtError);
      });
    } catch (caughtError) {
      console.warn('Failed to register the service worker', caughtError);
    }
  };

  // Defer until after first paint so the worker does not compete with the
  // initial bundle for bandwidth; without a window (SSR) register at once.
  if (typeof globalRef.addEventListener === 'function') {
    globalRef.addEventListener('load', register);
  } else {
    register();
  }
};
